// CurrencyProvider.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { currencies } from 'Common/constants/currencies';
import { GetCurrencyChange } from 'helpers/Currencies/CurrencyChange';
import { MessageType } from '../Messages/messageTypes';
import { useNotifications } from '../Notifications/NotificationProvider';
import { useTranslationContext } from '../Translation/TranslationContextType ';
import { useMessages } from '../Messages/MessageProvider';

const CurrencyContext = createContext<{
  currency: {
    value: string;
    rate: number;
    setCurrency: (currency: string) => void;
    on: (callback: () => void) => void;
    off: (callback: () => void) => void;
  };
}>({
  currency: {
    value: currencies.USD,
    rate: 1,
    setCurrency: () => {},
    on: () => {},
    off: () => {},
  },
});

let listeners: (() => void)[] = [];

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currencyValue, setCurrencyValue] = useState(currencies.USD);
  const [rate, setRate] = useState(1);

  // Ensure these hooks are working correctly
  const { setMessages, showMessage } = useMessages();
  const { setNotifications } = useNotifications();
  const { t } = useTranslationContext();

  useEffect(() => {
    if (!setMessages || !showMessage || !setNotifications || !t) {
      console.error("Hooks are not initialized correctly.");
      return;
    }

    if (currencyValue === currencies.MXN) {
      (async () => {
        const exchangeRate = await GetCurrencyChange(setMessages, setNotifications);
        if (exchangeRate <= 0) {
          showMessage(t("CurrencyDropdown.Error"), MessageType.Error, t("CurrencyDropdown.TitleError"));
          setCurrencyValue(currencies.USD);
          return;
        }
        setRate(exchangeRate);
      })();
    } else {
      setRate(1);
    }

    listeners.forEach(callback => callback());
  }, [currencyValue, setMessages, setNotifications, showMessage, t]);

  const on = (callback: () => void) => {
    listeners.push(callback);
  };

  const off = (callback: () => void) => {
    listeners = listeners.filter(listener => listener !== callback);
  };

  const currency = {
    value: currencyValue,
    rate,
    setCurrency: setCurrencyValue,
    on,
    off,
  };

  return (
    <CurrencyContext.Provider value={{ currency }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);